import { combineReducers, AnyAction } from "redux";
import {
  PagedResponseEmpty,
  ValidationError,
} from "@ofqual/portal-core/core/types";
import {
  LOADED_USERS,
  LOADING_USERS,
  LOADED_USER,
  LOADING_USER,
  UPDATING_USER,
  ACTIVATING_USER,
  CREATING_USER_VALIDATION_FAIL,
  CREATING_USER,
  CREATED_USER,
  SAVED_USER,
  RESETTING_PASSWORD,
  UPDATING_USER_VALIDATION_FAIL,
} from "./actions";
import {
  UsersState,
  UserState,
  CreateUserState,
  UsersStatus,
  UserStatus,
  CreateUserStatus,
} from "./state";
import { failedCrudResult, noCrudResult, successCrudResult } from "./types";

const usersInitialState: UsersState = {
  status: UsersStatus.notLoaded,
  response: PagedResponseEmpty,
  hasData: false,
};

const users = (
  state: UsersState = usersInitialState,
  action: AnyAction
): UsersState => {
  const { type, payload } = action;
  if (type === LOADING_USERS) {
    return {
      ...state,
      status: UsersStatus.loading,
    };
  }
  if (type === LOADED_USERS) {
    return {
      ...state,
      status: UsersStatus.loaded,
      response: payload,
      hasData: true,
    };
  }
  return state;
};

const userInitialState: UserState = {
  status: UserStatus.notLoaded,
  user: null,
  lastCrudResult: noCrudResult(),
};

const user = (
  state: UserState = userInitialState,
  action: AnyAction
): UserState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_USER:
    case ACTIVATING_USER:
    case RESETTING_PASSWORD:
      return {
        ...state,
        status: UserStatus.loading,
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_USER:
      return {
        ...state,
        status: UserStatus.updating,
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_USER_VALIDATION_FAIL:
      return {
        ...state,
        lastCrudResult: failedCrudResult(payload as ValidationError),
        status: UserStatus.loaded,
      };
    case LOADED_USER:
      return {
        ...state,
        status: UserStatus.loaded,
        user: payload,
      };
    case SAVED_USER:
      return {
        ...state,
        status: UserStatus.loaded,
        user: payload,
        lastCrudResult: successCrudResult(),
      };
    default:
      return state;
  }
};

const createUserInitialState: CreateUserState = {
  status: CreateUserStatus.inputting,
  lastCrudResult: noCrudResult(),
};

const createUser = (
  state: CreateUserState = createUserInitialState,
  action: AnyAction
): CreateUserState => {
  const { type, payload } = action;
  if (type === CREATING_USER) {
    return {
      ...state,
      status: CreateUserStatus.creating,
    };
  }
  if (type === CREATING_USER_VALIDATION_FAIL) {
    return {
      ...state,
      lastCrudResult: failedCrudResult(payload as ValidationError),
      status: CreateUserStatus.inputting,
    };
  }
  if (type === CREATED_USER) {
    return {
      ...state,
      // rather than successCrudResult() we just reset the form for the next time we visit
      //  (we get taken away from the form on success so no UI needs to respond to a success)
      lastCrudResult: noCrudResult(),
      status: CreateUserStatus.inputting,
    };
  }
  return state;
};

export const rootReducer = combineReducers({
  users,
  user,
  createUser,
});
